/**
 * Making the engine break the systems where the score says to.
 *
 * A break is already a fact of the document -- {@link GlobalMeasure.breakBefore}, set from
 * the Bar menu, written to and read back from MusicXML -- and until this existed the
 * engine was never told about it, so asking for a new system changed the file and nothing
 * on the page.
 *
 * Telling it is not one option, because of how Verovio 6.3.0 reads a break (measured, not
 * assumed):
 *
 * - `auto`, and `smart` as it comes, discard an encoded system break whose system would
 *   come out under two thirds full. A user who breaks after two bars is asking for a short
 *   system, so that is exactly the case that gets dropped.
 * - `smart` with the threshold at zero keeps every encoded system break and lays the rest
 *   of the piece out itself. It ignores a page break entirely.
 * - `encoded` keeps both, but then lays out nothing at all: a stretch with no break in it
 *   is squeezed onto one system however long it is. It does still start a new page when
 *   the one it is on is full, which is the part that makes the second pass below work.
 *
 * So a score that only breaks systems is drawn once, `smart` at zero. A score that breaks
 * a page is drawn twice: the first pass is that same `smart` layout, and what it chose is
 * read back out of the engine's own MEI and written into the score as breaks of its own.
 * The second pass is then `encoded` over a score where every system break is stated, which
 * is the one mode that obeys the page breaks -- and since the system breaks it obeys are
 * the ones the engine itself picked, the page looks as it did, cut where it was asked.
 *
 * The first pass is given the page breaks as system breaks, so that the system before one
 * is laid out as a system that ends there rather than being cut in half by the page in the
 * second pass -- a bar left over on its own at the foot of a page is what that looks like.
 *
 * An engine that cannot hand its MEI back draws a page break as the system break it was
 * turned into for that first pass. That is the safe way to fail: the music is all there
 * and laid out, and it breaks where it was asked to, on one long page.
 */

import type { Score } from "../document/types";
import type { RenderOptions } from "./port";

/** What a score asks of the page, which decides how many passes it takes to draw. */
export interface Breaks {
  readonly system: boolean;
  readonly page: boolean;
}

/** The breaks a score encodes. */
export function breaksIn(score: Score): Breaks {
  let system = false;
  let page = false;
  for (const measure of score.measures) {
    if (measure.breakBefore === "system") system = true;
    else if (measure.breakBefore === "page") page = true;
  }
  return { system, page };
}

/**
 * How the engine should be told to break, for a score asking for `breaks`.
 *
 * A score with nothing encoded is left on `auto`: the engine has a better idea of where a
 * system should end than an empty field does, and saying `smart` to a document with no
 * break in it only makes it complain that there was nothing to be smart about.
 */
export function breakMode(breaks: Breaks): NonNullable<RenderOptions["breaks"]> {
  return breaks.system || breaks.page ? "smart" : "auto";
}

/**
 * Where each system started, from the engine's page-based MEI, as bar numbers from zero.
 *
 * Page-based MEI is the layout the engine settled on rather than the document it read:
 * its bars are wrapped in the `<system>` they were drawn in, in order, so counting them is
 * enough and no id has to survive the trip.
 */
export function systemStarts(mei: string): readonly number[] {
  const starts: number[] = [];
  let seen = 0;
  for (const chunk of mei.split("<system").slice(1)) {
    starts.push(seen);
    seen += (chunk.match(/<measure\b/gu) ?? []).length;
  }
  return starts;
}

/**
 * The same score with those bars stated as system breaks.
 *
 * A bar the user made a page break stays one -- a page break is a system break and more,
 * and the engine treats it so -- and the first bar of the piece is left alone, since there
 * is no system before it to break out of.
 */
export function withSystemBreaks(score: Score, starts: readonly number[]): Score {
  const wanted = new Set(starts);
  const measures = score.measures.map((measure, index) => {
    if (measure.breakBefore === "page") return measure;
    const breakBefore = index > 0 && wanted.has(index) ? ("system" as const) : null;
    return measure.breakBefore === breakBefore ? measure : { ...measure, breakBefore };
  });
  return { ...score, measures };
}

/**
 * The same score with its page breaks written as system breaks.
 *
 * This is what the first pass is drawn from. A page break is a system break and more, so
 * the system before one has to end there whether or not the page does, and the engine only
 * reads the `and more` part in a mode that would lay out nothing else.
 */
export function asSystemBreaks(score: Score): Score {
  const measures = score.measures.map((measure) =>
    measure.breakBefore === "page" ? { ...measure, breakBefore: "system" as const } : measure,
  );
  return { ...score, measures };
}
